import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Copy, AlertCircle, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SpinnerCenter } from "@/components/Spinner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  listPermanentStaffInvitations,
  createPermanentStaffInvitation,
  deletePermanentStaffInvitation,
  listStores,
} from "@/lib/api/admin";
import type { PermanentStaffInvitation } from "@/lib/api/types";

export function StaffInvitationsPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [toDelete, setToDelete] = useState<PermanentStaffInvitation | null>(null);

  const {
    data: invitations = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["permanent-staff-invitations"],
    queryFn: () => listPermanentStaffInvitations(user!),
    enabled: !!user,
  });

  const { data: storesData } = useQuery({
    queryKey: ["stores-all"],
    queryFn: () => listStores(user!, { limit: 200 }),
    enabled: !!user,
  });
  const stores = storesData?.data ?? [];

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["permanent-staff-invitations"] });

  const createMut = useMutation({
    mutationFn: () => createPermanentStaffInvitation(user!, selectedStoreId),
    onSuccess: () => {
      invalidate();
      setSelectedStoreId("");
      toast.success("Permanent code created");
    },
    onError: () => toast.error("Failed to create invitation"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePermanentStaffInvitation(user!, id),
    onSuccess: () => {
      invalidate();
      setToDelete(null);
      toast.success("Invitation deleted");
    },
    onError: () => {
      setToDelete(null);
      toast.error("Failed to delete invitation");
    },
  });

  function copyCode(code: string) {
    navigator.clipboard.writeText(code).then(
      () => toast.success("Code copied"),
      () => toast.error("Failed to copy")
    );
  }

  if (isLoading) return <SpinnerCenter />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load invitations</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">Staff Invitations</h1>
        <p className="text-sm text-muted-foreground">
          Permanent, reusable 6-digit codes for joining a store as staff. Codes never expire and can
          be shared with testers (e.g. Apple app review).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Create Permanent Code</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a store…" />
              </SelectTrigger>
              <SelectContent>
                {stores.map((store) => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              disabled={!selectedStoreId || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              <Plus className="size-4 mr-1" />
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active Codes ({invitations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {invitations.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
              <KeyRound className="size-8 opacity-40" />
              <p className="text-sm">No permanent codes yet.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {invitations.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                >
                  <code className="font-mono text-base font-semibold tracking-widest bg-muted px-2 py-0.5 rounded">
                    {inv.code}
                  </code>
                  <span className="flex-1 text-sm text-muted-foreground truncate">
                    {inv.store_name}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(inv.created_at).toLocaleDateString()}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyCode(inv.code)}
                    className="p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Copy code"
                  >
                    <Copy className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setToDelete(inv)}
                    className="p-1 text-muted-foreground hover:text-destructive"
                    aria-label={`Delete code ${inv.code}`}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Delete invitation code?"
        description={`Code "${toDelete?.code}" for ${toDelete?.store_name} will be revoked. Anyone using it after this will get an error.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate(toDelete!.id)}
      />
    </div>
  );
}
